import React, { useState, useEffect } from 'react';
import './ScienceLab.css';

// Import images for Physics Lab
import physicsLab1 from '../assets/physics (1).webp';
import physicsLab2 from '../assets/physics (2).webp';
import physicsLab3 from '../assets/physics (3).webp';
import physicsLab4 from '../assets/physics (4).webp';

// Import images for Chemistry Lab
import chemistryLab1 from '../assets/chemistry (1).webp';
import chemistryLab2 from '../assets/chemistry (2).webp';
import chemistryLab3 from '../assets/chemistry (3).webp';
import chemistryLab4 from '../assets/chemistry (4).webp';

// Import images for Biology Lab
import biologyLab1 from '../assets/bio (1).webp';
import biologyLab2 from '../assets/bio (2).webp';
import biologyLab3 from '../assets/bio (3).webp';
import biologyLab4 from '../assets/bio (4).webp';

const ScienceLab = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [physicsIndex, setPhysicsIndex] = useState(0);
  const [chemistryIndex, setChemistryIndex] = useState(0);
  const [biologyIndex, setBiologyIndex] = useState(0);

  const physicsImages = [physicsLab1, physicsLab2, physicsLab3, physicsLab4];
  const chemistryImages = [chemistryLab1, chemistryLab2, chemistryLab3, chemistryLab4];
  const biologyImages = [biologyLab1, biologyLab2, biologyLab3, biologyLab4];

  useEffect(() => {
    setIsVisible(true);
    
    // Auto-slide for Physics
    const physicsInterval = setInterval(() => {
      setPhysicsIndex((prev) => (prev === physicsImages.length - 1 ? 0 : prev + 1));
    }, 3000);

    // Auto-slide for Chemistry
    const chemistryInterval = setInterval(() => {
      setChemistryIndex((prev) => (prev === chemistryImages.length - 1 ? 0 : prev + 1));
    }, 3000);

    // Auto-slide for Biology
    const biologyInterval = setInterval(() => {
      setBiologyIndex((prev) => (prev === biologyImages.length - 1 ? 0 : prev + 1));
    }, 3000);

    return () => {
      clearInterval(physicsInterval);
      clearInterval(chemistryInterval);
      clearInterval(biologyInterval);
    };
  }, []);

  return (
    <div className="sciencelab-container">
      {/* Banner Section with Background Image */}
      <section className="inner-bannerlib">
        <div className="banner-container">
          <h1>SCIENCE LAB</h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="breadcrumb-section">
        <div className="breadcrumb-container">
          <ul className="breadcrumb-list">
            <li><a href="/">Home</a></li>
            <li>/</li>
            <li>Science Lab</li>
          </ul>
        </div>
      </section>

      {/* Main Content */}
      <div className="sciencelab-main-content">
        
        {/* Science Lab Introduction */}
        <div className={`sciencelab-intro-section ${isVisible ? 'sciencelab-visible' : ''}`}>
          <h2 className="sciencelab-main-heading">SCIENCE LAB</h2>
          <p className="sciencelab-intro-text">
            A scientific temperament is nurtured through fully equipped and suitably designed laboratories for Physics, 
            Chemistry and Biology. Students are encouraged to use the facilities for enhancing their innovative and scientific skills.
          </p>
        </div>

        {/* Physics Lab Section */}
        <div className="sciencelab-section">
          <div className="sciencelab-content-wrapper">
            {/* Left Side - Text Content */}
            <div className={`sciencelab-text-content ${isVisible ? 'sciencelab-visible' : ''}`}>
              <h2 className="sciencelab-section-heading">PHYSICS LAB</h2>
              
              <p className="sciencelab-section-text">
                Bihani Children's Academy is proud to have a sophisticated Physics Laboratory with the latest state-of-the 
                art facilities that equip the students to have a hands on experience. The Flat-topped tables used as student 
                workstations allow multiple arrangements and combinations for laboratory work and small-group activities. The 
                equipment ranges from the commonly found lens, magnet, beam balance, spring balance, glass prism to the advanced 
                apparatus like voltmeter, ammeter and resistors and spectrometers to name a few.
              </p>

              <p className="sciencelab-section-text">
                The laboratory is also equipped with an Interactive board and an LCD projector that allows the teachers to 
                incorporate the latest technology in their lessons to arouse the interest and curiosity of the students. Furthermore, 
                the spacious physics lab is designed in a way to accommodate students comfortably.
              </p>
            </div>

            {/* Right Side - Image Slider */}
            <div className={`sciencelab-image-slider ${isVisible ? 'sciencelab-visible' : ''}`}>
              <div className="sciencelab-slider-container">
                {physicsImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Physics Lab ${index + 1}`}
                    className={`sciencelab-slider-image ${index === physicsIndex ? 'sciencelab-active' : ''}`}
                  />
                ))}
                
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-left"
                  onClick={() => setPhysicsIndex(physicsIndex === 0 ? physicsImages.length - 1 : physicsIndex - 1)}
                >
                  ‹
                </button>
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-right"
                  onClick={() => setPhysicsIndex(physicsIndex === physicsImages.length - 1 ? 0 : physicsIndex + 1)}
                >
                  ›
                </button>
                
                <div className="sciencelab-slider-dots">
                  {physicsImages.map((_, index) => (
                    <span
                      key={index}
                      className={`sciencelab-dot ${index === physicsIndex ? 'sciencelab-dot-active' : ''}`}
                      onClick={() => setPhysicsIndex(index)}
                    ></span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chemistry Lab Section */}
        <div className="sciencelab-section sciencelab-section-reverse">
          <div className="sciencelab-content-wrapper">
            {/* Left Side - Text Content */}
            <div className={`sciencelab-text-content ${isVisible ? 'sciencelab-visible' : ''}`}>
              <h2 className="sciencelab-section-heading">CHEMISTRY LAB</h2>
              
              <p className="sciencelab-section-text">
                The avant-garde Chemistry Laboratory provides a platform for students to nurture the budding scientist within them. 
                The laboratory is furnished with latest hi-tech interactive smart boards for a virtual real life learning experience. 
                The Lab is provided with state-of-the art devices like- Fume hood, Distillation unit, Electronic weighing machine, 
                Concealed gas line and the safety devices such as Firefighting equipment, First- Aid Kit etc.
              </p>

              <p className="sciencelab-section-text">
                The acids and other reactive chemicals are stored in separate cupboards as a further precautionary measure. The 
                students are provided with separate workstations with individual burners and plug points. The apparatus includes 
                the commonplace strikers, mortar and pestle, clay triangles, watch glasses, crucibles, crucible tongs, chemical 
                spoon, stirring rods, filter funnels and a variety of stoppers.
              </p>
            </div>

            {/* Right Side - Image Slider */}
            <div className={`sciencelab-image-slider ${isVisible ? 'sciencelab-visible' : ''}`}>
              <div className="sciencelab-slider-container">
                {chemistryImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Chemistry Lab ${index + 1}`}
                    className={`sciencelab-slider-image ${index === chemistryIndex ? 'sciencelab-active' : ''}`}
                  />
                ))}
                
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-left"
                  onClick={() => setChemistryIndex(chemistryIndex === 0 ? chemistryImages.length - 1 : chemistryIndex - 1)}
                >
                  ‹
                </button>
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-right"
                  onClick={() => setChemistryIndex(chemistryIndex === chemistryImages.length - 1 ? 0 : chemistryIndex + 1)}
                >
                  ›
                </button>
                
                <div className="sciencelab-slider-dots">
                  {chemistryImages.map((_, index) => (
                    <span
                      key={index}
                      className={`sciencelab-dot ${index === chemistryIndex ? 'sciencelab-dot-active' : ''}`}
                      onClick={() => setChemistryIndex(index)}
                    ></span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Biology Lab Section */}
        <div className="sciencelab-section">
          <div className="sciencelab-content-wrapper">
            {/* Left Side - Text Content */}
            <div className={`sciencelab-text-content ${isVisible ? 'sciencelab-visible' : ''}`}>
              <h2 className="sciencelab-section-heading">BIOLOGY LAB</h2>
              
              <p className="sciencelab-section-text">
                "Without theory, practice is but routine born of habit. Theory alone can bring forth and develop the spirit of 
                invention. ... [Do not] share the opinion of those narrow minds who disdain everything in science which has not 
                an immediate application. ... A theoretical discovery has but the merit of its existence: it awakens hope, and 
                that is all. But let it be cultivated, let it grow, and you will see what it will become." — Louis Pasteur
              </p>

              <p className="sciencelab-section-text">
                The Biology Laboratory is equipped with modern facilities to provide hands-on learning experiences. Students have 
                access to microscopes, specimen collections, models, and charts that help them understand the intricate details 
                of life sciences. The lab encourages scientific inquiry and experimentation, fostering a deeper understanding of 
                biological concepts.
              </p>
            </div>

            {/* Right Side - Image Slider */}
            <div className={`sciencelab-image-slider ${isVisible ? 'sciencelab-visible' : ''}`}>
              <div className="sciencelab-slider-container">
                {biologyImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Biology Lab ${index + 1}`}
                    className={`sciencelab-slider-image ${index === biologyIndex ? 'sciencelab-active' : ''}`}
                  />
                ))}
                
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-left"
                  onClick={() => setBiologyIndex(biologyIndex === 0 ? biologyImages.length - 1 : biologyIndex - 1)}
                >
                  ‹
                </button>
                <button 
                  className="sciencelab-slider-arrow sciencelab-arrow-right"
                  onClick={() => setBiologyIndex(biologyIndex === biologyImages.length - 1 ? 0 : biologyIndex + 1)}
                >
                  ›
                </button>
                
                <div className="sciencelab-slider-dots">
                  {biologyImages.map((_, index) => (
                    <span
                      key={index}
                      className={`sciencelab-dot ${index === biologyIndex ? 'sciencelab-dot-active' : ''}`}
                      onClick={() => setBiologyIndex(index)}
                    ></span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ScienceLab;